import { DriveStep } from 'driver.js';

/**
 * Company Landing Tour
 * Guides users through the company overview/landing screen
 */
export function getCompanyLandingTour(): DriveStep[] {
  return [
    {
      element: '[data-tour="company-banner"]',
      popover: {
        title: 'Welcome to TPI Insurance! 👋',
        description:
          "Let's take a quick tour to help you get the best insurance quotes for your trucking business.",
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="company-name"]',
      popover: {
        title: 'Your Company Information',
        description:
          "This is your company profile. We've pre-filled it with information from your DOT number. You can edit it anytime.",
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="edit-company-btn"]',
      popover: {
        title: 'Edit Your Details',
        description: 'Click here if you need to update your company information.',
        side: 'left',
        align: 'start',
      },
    },
    {
      element: '[data-tour="get-quote-btn"]',
      popover: {
        title: 'Ready to Get Quotes?',
        description:
          'When your information looks good, click "Get A Quote" to continue to the next step: adding your vehicles.',
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/**
 * Company Details Tour
 * Guides users through the company details form
 */
export function getCompanyDetailsTour(): DriveStep[] {
  return [
    {
      popover: {
        title: 'Company Details Form ✏️',
        description:
          'Fill out your company information carefully. Accurate details help us get you the best insurance rates.',
      },
    },
    {
      element: '[data-tour="company-name-input"]',
      popover: {
        title: 'Company Name',
        description: 'Enter your legal business name exactly as it appears on official documents.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="dba-input"]',
      popover: {
        title: 'DBA (Doing Business As)',
        description:
          'If your company operates under a different name, enter it here. Otherwise, you can skip this field.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="business-start-date"]',
      popover: {
        title: 'Business Start Date',
        description:
          'When did your trucking business officially start? This helps determine your experience level.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="dot-number-input"]',
      popover: {
        title: 'DOT Number',
        description:
          'Your Department of Transportation number. We use this to verify your company and driving history.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="mc-number-input"]',
      popover: {
        title: 'MC Number (Optional)',
        description:
          'Your Motor Carrier number, if you have one. This is optional but helpful for complete quotes.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="continue-btn"]',
      popover: {
        title: 'Continue to Next Step 🚀',
        description:
          "Once you've filled out all required fields, click here to navigate to address screen.",
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/**
 * Company Address Tour
 * Guides users through the company address form
 */
export function getCompanyAddressTour(): DriveStep[] {
  return [
    {
      popover: {
        title: 'Company Address Information 📍',
        description:
          "Now let's confirm your business addresses. These are important for accurate insurance coverage.",
      },
    },
    {
      element: '[data-tour="same-address-question"]',
      popover: {
        title: 'Same Address?',
        description:
          'Tell us if your garaging address (where vehicles are kept) is the same as your mailing address.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="garaging-address-input"]',
      popover: {
        title: 'Garaging Address',
        description:
          'This is where your vehicles are parked when not in use. Click to select the address on the map.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="mailing-address-input"]',
      popover: {
        title: 'Mailing Address',
        description:
          'Where should we send important documents? If it\'s the same as garaging, just select "Yes" above.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="previous-authority-question"]',
      popover: {
        title: 'Previous Authority',
        description:
          'Have you operated under a different authority before? This affects your experience rating.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="save-continue-btn"]',
      popover: {
        title: 'Save and Continue 🎯',
        description:
          'Once all addresses are filled in, click here to save and return to your company summary.',
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/**
 * Vehicle Start Tour
 * Guides users through the VIN entry screen
 */
export function getVehicleStartTour(): DriveStep[] {
  return [
    {
      popover: {
        title: 'Vehicle Information 🚛',
        description:
          "Let's add your vehicles. Providing accurate VIN and vehicle details ensures FMCSA compliance and proper coverage.",
      },
    },
    {
      element: '[data-tour="vin-input"]',
      popover: {
        title: 'Enter Your VIN',
        description:
          'The VIN (Vehicle Identification Number) is required for DOT compliance and insurance verification. Enter all 17 characters.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="upload-vin-card"]',
      popover: {
        title: 'Upload VIN Photo (Optional)',
        description:
          "You can also upload a photo of your VIN for faster entry. We'll extract the VIN automatically.",
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="vehicle-start-next-btn"]',
      popover: {
        title: 'Continue',
        description:
          "Once you've entered a valid VIN, click Next to add vehicle details required for your insurance quote.",
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/**
 * Vehicle Bypass Tour
 * Guides users through the vehicle summary/list screen
 */
export function getVehicleBypassTour(): DriveStep[] {
  return [
    {
      popover: {
        title: 'Vehicle Summary 📋',
        description:
          'Review all your vehicles here. DOT compliance requires accurate vehicle records for your insurance application.',
      },
    },
    {
      element: '[data-tour="vehicle-list"]',
      popover: {
        title: 'Your Vehicles',
        description:
          'All added vehicles are listed here. Each vehicle must meet FMCSA requirements. You can edit or remove them anytime.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="vehicle-card-actions"]',
      popover: {
        title: 'Edit or Delete',
        description:
          'Need to update vehicle details or remove a vehicle? Use the edit and delete buttons on each card.',
        side: 'right',
        align: 'center',
      },
    },
    {
      element: '[data-tour="add-vehicle-btn"]',
      popover: {
        title: 'Add Another Vehicle',
        description:
          'Operating multiple vehicles? Click here to add more. All vehicles in your fleet need to be listed for compliance.',
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/**
 * Vehicle Add/Edit Tour
 * Guides users through the vehicle details form
 */
export function getVehicleAddTour(): DriveStep[] {
  return [
    {
      popover: {
        title: 'Vehicle Details 📝',
        description:
          'Complete all required vehicle information for DOT compliance and accurate insurance quotes.',
      },
    },
    {
      element: '[data-tour="vehicle-type-switch"]',
      popover: {
        title: 'Vehicle Type',
        description:
          'Select whether this is a truck or trailer. Each type has different insurance and compliance requirements.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="vehicle-basic-info"]',
      popover: {
        title: 'Basic Information',
        description:
          'Year, Make, Model, and Class are pre-filled from your VIN. Verify accuracy as these affect your coverage and rates.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="vehicle-stated-value"]',
      popover: {
        title: 'Vehicle Values',
        description:
          'Enter the stated value and any equipment value. Accurate valuations ensure proper coverage limits for claims.',
        side: 'right',
        align: 'center',
      },
    },
    {
      element: '[data-tour="vehicle-save-btn"]',
      popover: {
        title: 'Save Vehicle',
        description:
          'Once all required fields are completed, save this vehicle to continue with your application.',
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/**
 * Driver License Entry Tour
 * Guides users through the driver's license entry screen
 */
export function getDriverLicenseTour(): DriveStep[] {
  return [
    {
      popover: {
        title: 'Driver Information 👨‍✈️',
        description:
          "Let's add your drivers. Driver information is required for DOT compliance and accurate insurance quotes.",
      },
    },
    {
      element: '[data-tour="upload-driver-license-card"]',
      popover: {
        title: 'Upload License Photo (Optional)',
        description:
          "You can upload a photo of the driver's license for faster entry. We'll extract the license details automatically.",
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: '[data-tour="dl-number-input"]',
      popover: {
        title: 'License Number',
        description:
          "Enter the driver's license number. This is required for insurance verification and DOT compliance.",
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="dl-state-input"]',
      popover: {
        title: 'License State',
        description:
          'Select the state where the license was issued. This affects coverage requirements and rates.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="driver-license-next-btn"]',
      popover: {
        title: 'Continue',
        description:
          'Once license details are entered, click Next to add complete driver information.',
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/**
 * Driver Bypass Tour
 * Guides users through the driver summary/list screen
 */
export function getDriverBypassTour(): DriveStep[] {
  return [
    {
      popover: {
        title: 'Driver Summary 📋',
        description:
          'Review all your drivers here. DOT compliance requires accurate driver records for your insurance application.',
      },
    },
    {
      element: '[data-tour="driver-list"]',
      popover: {
        title: 'Your Drivers',
        description:
          'All added drivers are listed here. Each driver must meet FMCSA requirements. You can edit or remove them anytime.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="driver-card-actions"]',
      popover: {
        title: 'Edit or Delete',
        description:
          'Need to update driver details or remove a driver? Use the edit and delete buttons on each card.',
        side: 'right',
        align: 'center',
      },
    },
    {
      element: '[data-tour="add-driver-btn"]',
      popover: {
        title: 'Add Another Driver',
        description:
          'Operating with multiple drivers? Click here to add more. All drivers in your fleet need to be listed for compliance.',
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/**
 * Driver Add/Edit Tour
 * Guides users through the driver details form
 */
export function getDriverAddTour(): DriveStep[] {
  return [
    {
      popover: {
        title: 'Driver Details 📝',
        description:
          'Complete all required driver information for DOT compliance and accurate insurance quotes.',
      },
    },
    {
      element: '[data-tour="driver-name-fields"]',
      popover: {
        title: 'Driver Name',
        description:
          "Enter the driver's full legal name as it appears on their license. This must match official records.",
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="driver-experience"]',
      popover: {
        title: 'Driving Experience',
        description:
          'Enter total commercial driving experience. More experience typically results in better insurance rates.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="driver-date-of-hire"]',
      popover: {
        title: 'Date of Hire',
        description:
          'When did this driver start with your company? This date must be after their date of birth and affects your coverage.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="driver-accidents"]',
      popover: {
        title: 'Total Accidents',
        description:
          "Enter the total number of accidents in the driver's record. This directly impacts insurance rates.",
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="driver-violations"]',
      popover: {
        title: 'Total Violations',
        description:
          'Enter the total number of traffic violations. Clean records help secure better insurance rates.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="driver-suspensions"]',
      popover: {
        title: 'License Suspensions',
        description:
          'Enter any license suspensions on record. This is critical for DOT compliance and underwriting.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="driver-major-violations"]',
      popover: {
        title: 'Major Violations',
        description:
          'Enter major violations (DUI, reckless driving, etc.). These significantly affect coverage eligibility.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: '[data-tour="driver-save-btn"]',
      popover: {
        title: 'Save Driver',
        description:
          'Once all required fields are completed, save this driver to continue with your application.',
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/**
 * Quotes Success Tour
 * Guides users through the successful quote screen
 */
export function getQuotesSuccessTour(): DriveStep[] {
  return [
    {
      popover: {
        title: 'Congratulations! 🎉',
        description:
          "You've completed the quote process! Here are your insurance options from top carriers.",
      },
    },
    {
      element: '[data-tour="quote-cards"]',
      popover: {
        title: 'Your Quote Options',
        description:
          'Compare quotes from different insurance carriers. Each card shows coverage details and pricing.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="select-quote-btn"]',
      popover: {
        title: 'Select Your Quote',
        description:
          'Found the right quote? Click to proceed with your insurance application and get covered!',
        side: 'top',
        align: 'center',
      },
    },
  ];
}

/**
 * Freight (Commodities) Tour
 * Guides users through selecting commodities and setting percentages
 */
export function getFreightTour(): DriveStep[] {
  return [
    {
      element: '[data-tour="freight-title"]',
      popover: {
        title: 'Freight & Commodities 📦',
        description:
          'Tell us what you haul! This helps us find the best insurance coverage for your specific cargo types.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="add-commodities-btn"]',
      popover: {
        title: 'Add Your Commodities',
        description:
          'Click here to select the types of freight you haul. You can choose multiple commodity types.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: '[data-tour="commodities-list"]',
      popover: {
        title: 'Adjust Percentages',
        description:
          'Use the sliders to set what percentage of your hauls are for each commodity type. The total must equal 100%.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="freight-toast"]',
      popover: {
        title: 'Track Your Progress',
        description:
          "This indicator shows whether your percentages add up to 100%. Once it turns green, you're ready to proceed!",
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="freight-next-btn"]',
      popover: {
        title: 'Continue to Final Details',
        description:
          'When your commodities total 100%, click Next to move on to the final details and get your insurance quotes!',
        side: 'top',
        align: 'center',
      },
    },
  ];
}
